import '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { setupEdgeSlider } from './ch-edge-slider.js';
import { beginCartLinesUpdate, controllableLineForVariant, variantQuantity } from './standard-cart-events.js';

const ROOT = window.Shopify?.routes?.root || '/';
const CART_ADD_URL = `${ROOT}cart/add.js`;
const CART_CHANGE_URL = `${ROOT}cart/change.js`;
const CART_URL = `${ROOT}cart.js`;
const SUCCESS_DELAY = 1400;

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `Cart request failed: ${response.status}`);
  }

  return data;
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

function getVariantQuantity(cart, variantId) {
  return variantQuantity(cart, variantId);
}

function getEligibleSubtotal(cart, giftVariantId) {
  return (cart.items || []).reduce((total, item) => {
    if (giftVariantId && String(item.variant_id) === String(giftVariantId)) return total;
    return total + (Number(item.final_line_price) || 0);
  }, 0);
}

class CHProductUpsells extends HTMLElement {
  #abortController = new AbortController();
  #configuredSliders = new WeakSet();
  #interactedSliders = new WeakSet();
  #mutationObserver = new MutationObserver(() => {
    this.#syncVisibility();
    this.#setupSlideshowControls();
    this.#syncSelectionUI();
  });
  #isUpdatingGift = false;
  #feedbackTimeouts = new WeakMap();
  #cart = null;
  #slideshowLayoutFrame = 0;
  #slideshowLayoutTimeout = 0;

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal, capture: true });
    this.addEventListener('input', this.#handleMessageInput, { signal });
    document.addEventListener('change', this.#handleProductFormChange, { signal });
    this.closest('.shopify-section')?.addEventListener('product:variant-rendered', this.#handleVariantUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleStandardCartUpdate, { signal });
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    this.#syncVisibility();
    this.#syncFromCart();
    this.#setupSlideshowControls();
    this.#syncSelectionUI();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#abortController.abort();
    if (this.#slideshowLayoutFrame) window.cancelAnimationFrame(this.#slideshowLayoutFrame);
    if (this.#slideshowLayoutTimeout) window.clearTimeout(this.#slideshowLayoutTimeout);
  }

  get #giftVariantId() {
    return this.dataset.freeGiftVariantId || '';
  }

  get #giftThreshold() {
    return Number.parseInt(this.dataset.freeGiftThreshold || '0', 10) || 0;
  }

  get #isThemeManagedGift() {
    return this.dataset.freeGiftMode === 'theme' && this.dataset.freeGiftEnabled === 'true' && Boolean(this.#giftVariantId);
  }

  get #selectWithMain() {
    return this.dataset.submitWithMain === 'true';
  }

  #handleClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const setButton = target.closest('[data-ch-complete-set-button]');
    if (setButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#selectCompleteSet(setButton.getAttribute('aria-pressed') !== 'true');
      return;
    }

    const opener = target.closest('[data-ch-upsell-open]');
    if (opener && this.#selectWithMain) {
      event.preventDefault();
      event.stopPropagation();
      const card = opener.closest('[data-ch-product-upsell-card]');
      if (card) this.#openSelectionDialog(card, opener.hasAttribute('data-ch-upsell-note'));
      return;
    }

    const confirm = target.closest('[data-ch-product-upsell-dialog-confirm]');
    if (confirm instanceof HTMLButtonElement) {
      const card = confirm.closest('[data-ch-product-upsell-card]');
      if (card instanceof HTMLElement) this.#confirmSelection(card);
      return;
    }

    const button = target.closest('[data-ch-product-upsell-toggle]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const card = button.closest('[data-ch-product-upsell-card]');
    if (!(card instanceof HTMLElement)) return;

    if (card.dataset.kind === 'free_gift') return;

    if (this.#selectWithMain) {
      this.#toggleSelection(card, button);
      return;
    }

    await this.#addCard(card, button);
  };

  #handleMessageInput = (event) => {
    const message = event.target;
    if (!this.#selectWithMain || !(message instanceof HTMLTextAreaElement)) return;
    if (!message.matches('[data-ch-product-upsell-message]') || !message.value.trim()) return;
    const card = message.closest('[data-ch-product-upsell-card]');
    if (!card || card.dataset.selected === 'true') return;

    // The celebration dialog has no confirm button. Writing a note must also
    // include its card in the main form's selected cart lines.
    if (this.dataset.completeSet === 'true') {
      this.#selectCompleteSet(true);
    } else {
      card.dataset.selected = 'true';
      card.querySelector('[data-ch-product-upsell-toggle]')?.setAttribute('aria-pressed', 'true');
      this.#syncSelectionUI();
    }
  };

  #handleProductFormChange = (event) => {
    if (!this.#selectWithMain || !(event.target instanceof Element)) return;
    if (event.target.matches('[data-ch-complete-set-toggle]') && this.contains(event.target)) {
      this.#selectCompleteSet(event.target.checked);
    }
    const productDetails = this.closest('.product-details');
    if (productDetails && productDetails.contains(event.target)) this.#syncSelectionUI();
  };

  #handleVariantUpdate = (event) => {
    if (!this.#selectWithMain) return;
    const productId = event.detail?.data?.productId;
    if (productId && String(productId) !== this.dataset.mainProductId) return;
    const variant = event.detail?.resource;
    if (!variant) return;
    this.dataset.mainPrice = String(Number(variant.price) || 0);
    this.#syncSelectionUI();
  };

  #toggleSelection(card, button) {
    if (this.dataset.completeSet === 'true') {
      this.#selectCompleteSet(card.dataset.selected !== 'true');
      return;
    }
    const isSelected = card.dataset.selected === 'true';
    if (isSelected) {
      card.dataset.selected = 'false';
      button.setAttribute('aria-pressed', 'false');
      this.#syncSelectionUI();
      return;
    }

    if (card.dataset.requiresMessage === 'true' || card.dataset.hasVariantOptions === 'true') {
      this.#openSelectionDialog(card, card.dataset.requiresMessage === 'true');
      return;
    }

    card.dataset.selected = 'true';
    button.setAttribute('aria-pressed', 'true');
    this.#syncSelectionUI();
  }

  #openSelectionDialog(card, focusMessage = false) {
    const dialog = this.#ensureSelectionDialog(card);
    if (!(dialog instanceof HTMLDialogElement)) return;
    const variant = dialog.querySelector('[data-ch-upsell-variant]');
    if (variant) variant.value = card.dataset.variantId;
    const opener = document.activeElement;
    const stop = (event) => event.stopPropagation();
    dialog.addEventListener('mousedown', stop);
    dialog.addEventListener('pointerdown', stop);
    dialog.addEventListener('close', () => {
      dialog.removeEventListener('mousedown', stop);
      dialog.removeEventListener('pointerdown', stop);
      opener?.focus({ preventScroll: true });
    }, { once: true });
    dialog.closest('dialog-component').showDialog();
    const slideshow = dialog.querySelector('.ch-product-upsell-card__detail-images');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.disabled = false;
      slideshow.current = -1;
      slideshow.select(0, undefined, { animate: false });
    }
    if (focusMessage) {
      const message = dialog.querySelector('[data-ch-product-upsell-message]');
      message?.focus({ preventScroll: true });
      message?.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }

  #ensureSelectionDialog(card) {
    const existingDialog = card.querySelector('[data-ch-product-upsell-dialog]');
    if (existingDialog instanceof HTMLDialogElement) return existingDialog;

    const template = card.querySelector('[data-ch-product-upsell-dialog-template]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    card.append(template.content.cloneNode(true));
    template.remove();
    const dialog = card.querySelector('[data-ch-product-upsell-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #confirmSelection(card) {
    const message = card.querySelector('[data-ch-product-upsell-message]');
    if (message instanceof HTMLTextAreaElement) message.setCustomValidity('');
    const option = card.querySelector('[data-ch-upsell-variant]')?.selectedOptions[0];
    if (option) {
      if (option.disabled) return;
      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price;
      card.querySelector('[data-ch-product-upsell-price]').textContent = this.#formatMoney(option.dataset.price);
    }
    card.dataset.selected = 'true';
    card.querySelector('[data-ch-product-upsell-toggle]')?.setAttribute('aria-pressed', 'true');
    if (this.dataset.completeSet === 'true') this.#selectCompleteSet(true);
    this.#closeSelectionDialog(card, false);
    this.#syncSelectionUI();
  }

  #closeSelectionDialog(card) {
    card.querySelector('dialog-component')?.closeDialog();
  }

  selectedLines() {
    if (!this.#selectWithMain) return [];
    return Array.from(
      this.querySelectorAll('[data-ch-product-upsell-card][data-select-with-main="true"][data-selected="true"]')
    )
      .map((card) => {
        const message = card.querySelector('[data-ch-product-upsell-message]');
        return {
          id: card.dataset.variantId,
          quantity: 1,
          properties:
            message instanceof HTMLTextAreaElement && message.value.trim()
              ? { 'Card message': message.value.trim() }
              : undefined,
        };
      })
      .filter((line) => line.id);
  }

  validateSelections() {
    const invalidCard = Array.from(
      this.querySelectorAll(
        '[data-ch-product-upsell-card][data-select-with-main="true"][data-selected="true"][data-requires-message="true"]'
      )
    ).find((card) => !card.querySelector('[data-ch-product-upsell-message]')?.value.trim());
    if (!(invalidCard instanceof HTMLElement)) return true;
    if (this.dataset.completeSet === 'true') {
      this.#openSelectionDialog(invalidCard, true);
      return false;
    }
    const button = invalidCard.querySelector('[data-ch-product-upsell-toggle]');
    if (button instanceof HTMLButtonElement) this.#toggleSelection(invalidCard, button);
    return false;
  }

  #handleCartUpdate = (event) => {
    const cart = event.detail?.resource;
    if (cart?.items) {
      this.#updateFromCart(cart);
      this.#syncThemeManagedGift(cart);
    } else {
      this.#syncFromCart();
    }
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleStandardCartUpdate = (event) => {
    event.promise
      .then(async ({ detail }) => {
        const cart = detail?.ajaxCart || (await this.#fetchCart());
        this.#updateFromCart(cart);
        if (event.detail?.source !== 'ch-product-upsells-free-gift') this.#syncThemeManagedGift(cart);
      })
      .catch(() => {});
  };

  async #addCard(card, button) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    this.#setCardUpdating(card, true);
    let standardUpdate;

    try {
      const sectionIds = getCartSectionIds();
      standardUpdate = beginCartLinesUpdate(this, {
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        detail: { source: 'ch-product-upsells', variantId },
      });
      const addResponse = await fetchJson(CART_ADD_URL, {
        method: 'POST',
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const cart = await this.#fetchCart();
      standardUpdate.resolve(cart, { sections: addResponse.sections || {}, itemCount: cart.item_count });

      this.#updateFromCart(cart);
      this.#showFeedback(card);
    } catch (error) {
      standardUpdate?.reject(error);
      console.error('Failed to add product upsell:', error);
      this.#setCardStatus(card, 'Could not add this item.');
    } finally {
      this.#setCardUpdating(card, false);
      button.blur();
    }
  }

  async #syncFromCart() {
    try {
      const cart = await this.#fetchCart();
      this.#updateFromCart(cart);
      this.#syncThemeManagedGift(cart);
    } catch (error) {
      console.error('Failed to sync product upsells:', error);
    }
  }

  async #fetchCart() {
    return fetchJson(CART_URL, { headers: { Accept: 'application/json' } });
  }

  #setupSlideshowControls() {
    const slides = this.querySelector('.ch-product-upsells__slideshow slideshow-slides');
    if (!(slides instanceof HTMLElement)) return;
    if (this.#configuredSliders.has(slides)) {
      this.#scheduleSlideshowLayoutSync(slides);
      return;
    }

    const configured = setupEdgeSlider({
      root: this,
      slideshowSelector: '.ch-edge-slider',
      progressSelector: '.ch-edge-slider__progress',
      progressBarClass: 'ch-edge-slider__progress-bar',
      edgeDataKey: 'chEdgeSliderEdge',
      signal: this.#abortController.signal,
    });

    if (configured) {
      const markInteracted = () => this.#interactedSliders.add(slides);
      const slideshow = slides.closest('slideshow-component');
      for (const type of ['pointerdown', 'keydown', 'wheel']) {
        slideshow.addEventListener(type, markInteracted, { signal: this.#abortController.signal, passive: true });
      }
      const observer = new ResizeObserver(() => {
        if (!this.#interactedSliders.has(slides)) slides.scrollTo({ left: 0, behavior: 'instant' });
      });
      observer.observe(slides);
      this.#abortController.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      slides.scrollTo({ left: 0, behavior: 'instant' });
      this.#configuredSliders.add(slides);
      this.#scheduleSlideshowLayoutSync(slides);
    }
  }

  #scheduleSlideshowLayoutSync(slides) {
    if (this.#slideshowLayoutFrame) window.cancelAnimationFrame(this.#slideshowLayoutFrame);
    if (this.#slideshowLayoutTimeout) window.clearTimeout(this.#slideshowLayoutTimeout);

    const sync = () => {
      if (!this.#interactedSliders.has(slides)) slides.scrollTo({ left: 0, behavior: 'instant' });
      slides.dispatchEvent(new Event('scroll'));
    };
    this.#slideshowLayoutFrame = window.requestAnimationFrame(() => {
      this.#slideshowLayoutFrame = 0;
      sync();
    });
    this.#slideshowLayoutTimeout = window.setTimeout(() => {
      this.#slideshowLayoutTimeout = 0;
      sync();
    }, 150);
  }

  #syncVisibility() {
    if (this.dataset.completeSet === 'true') {
      this.hidden = false;
      return;
    }
    const hasProductCard = Boolean(this.querySelector('[data-ch-product-upsell-card]:not([hidden])'));
    const hasValidUpsell = Boolean(
      this.querySelector('[data-ch-product-upsell-card][data-kind="upsell"]:not([hidden])')
    );
    const hasRewardPrompt = Boolean(this.querySelector('ch-cart-reward-product-card:not([hidden])'));
    const shouldHide = this.#selectWithMain ? !hasValidUpsell : !hasProductCard && !hasRewardPrompt;

    if (this.hidden !== shouldHide) this.hidden = shouldHide;
  }

  #updateFromCart(cart) {
    this.#cart = cart;
    const giftVariantId = this.#giftVariantId;
    const eligibleSubtotal = getEligibleSubtotal(cart, giftVariantId);
    const threshold = this.#giftThreshold;
    const isGiftEligible = threshold <= 0 || eligibleSubtotal >= threshold;

    this.querySelectorAll('[data-ch-product-upsell-card]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      if (this.#selectWithMain && card.dataset.kind !== 'free_gift') return;

      const quantity = getVariantQuantity(cart, card.dataset.variantId);
      card.dataset.quantity = String(quantity);
      card.dataset.selected = quantity > 0 ? 'true' : 'false';

      if (card.dataset.kind === 'free_gift') {
        card.dataset.eligible = isGiftEligible ? 'true' : 'false';
      }
    });

    const progress = this.querySelector('[data-ch-product-upsell-progress]');
    if (progress instanceof HTMLElement) {
      const ratio = threshold <= 0 ? 1 : Math.min(eligibleSubtotal / threshold, 1);
      progress.style.setProperty('--ch-product-upsell-progress', String(ratio));
    }

    const note = this.querySelector('[data-ch-product-upsell-gift-note]');
    if (note) {
      note.dataset.eligible = isGiftEligible ? 'true' : 'false';
    }

    this.#syncSelectionUI();
  }

  #selectCompleteSet(selected) {
    const checkbox = this.querySelector('[data-ch-complete-set-toggle]');
    if (checkbox) checkbox.checked = selected;
    this.querySelector('[data-ch-complete-set-button]')?.setAttribute('aria-pressed', String(selected));
    this.querySelectorAll('[data-ch-product-upsell-card]').forEach((card) => {
      card.dataset.selected = String(selected);
      card.querySelector('[data-ch-product-upsell-toggle]')?.setAttribute('aria-pressed', String(selected));
    });
    this.#syncSelectionUI();
  }

  #syncSelectionUI() {
    if (!this.#selectWithMain) return;
    const setPrice = this.querySelector('[data-ch-complete-set-price]');
    if (setPrice) {
      const total = Array.from(this.querySelectorAll('[data-ch-product-upsell-card]'))
        .reduce((sum, card) => sum + (Number(card.dataset.price) || 0), 0);
      const label = `+${this.#formatMoney(total)}`;
      if (setPrice.textContent !== label) setPrice.textContent = label;
    }
    const quantityInput = this.closest('.product-details')?.querySelector('product-form-component input[name="quantity"]');
    const quantity = Number.parseInt(quantityInput?.value || '1', 10) || 1;
    const mainPrice = (Number.parseInt(this.dataset.mainPrice || '0', 10) || 0) * quantity;
    const extrasPrice = Array.from(
      this.querySelectorAll('[data-ch-product-upsell-card][data-select-with-main="true"][data-selected="true"]')
    ).reduce((total, card) => total + (Number.parseInt(card.dataset.price || '0', 10) || 0), 0);
    const combinedPrice = mainPrice + extrasPrice;
    const selectedExtras = this.querySelectorAll(
      '[data-ch-product-upsell-card][data-select-with-main="true"][data-selected="true"]'
    ).length;
    const itemCount = quantity + selectedExtras;
    const formattedTotal = this.#formatMoney(combinedPrice);

    const addToCartText = this.closest('.product-details')?.querySelector(
      'product-form-component add-to-cart-component .add-to-cart-text__content'
    );
    const addToCartButton = addToCartText?.closest('button');
    const buttonText = selectedExtras > 0
      ? this.dataset.addAllLabel?.replace('__count__', String(itemCount)).replace('__total__', formattedTotal)
       : this.dataset.completeSet === 'true'
        ? this.dataset.mainOnlyLabel?.replace('__total__', formattedTotal)
        : this.dataset.baseAddToCartLabel;
    if (
      addToCartText &&
      addToCartButton instanceof HTMLButtonElement &&
      !addToCartButton.disabled &&
      buttonText &&
      addToCartText.textContent.trim() !== buttonText
    ) {
      addToCartText.textContent = buttonText;
    }

    const acceleratedCheckout = this.closest('.product-details')?.querySelector('.accelerated-checkout-block');
    if (acceleratedCheckout instanceof HTMLElement) {
      if (selectedExtras > 0 && !acceleratedCheckout.hidden) {
        acceleratedCheckout.hidden = true;
        acceleratedCheckout.dataset.chBundleSuppressed = 'true';
      } else if (selectedExtras === 0 && acceleratedCheckout.dataset.chBundleSuppressed === 'true') {
        acceleratedCheckout.hidden = false;
        delete acceleratedCheckout.dataset.chBundleSuppressed;
      }
    }

    const rewardPrompt = this.querySelector('ch-cart-reward-product-card[data-first-threshold-product-prompt="true"]');
    if (rewardPrompt instanceof HTMLElement) {
      if (rewardPrompt.dataset.chPreviewPrice === String(combinedPrice)) return;
      rewardPrompt.dataset.chPreviewPrice = String(combinedPrice);
      rewardPrompt.dataset.productPrice = String(combinedPrice);
      const marker = document.createElement('span');
      marker.hidden = true;
      rewardPrompt.append(marker);
      marker.remove();
    }
  }

  #formatMoney(cents) {
    const currency = this.#cart?.currency || window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  }

  async #syncThemeManagedGift(cart) {
    if (!this.#isThemeManagedGift || this.#isUpdatingGift) return;

    const giftVariantId = this.#giftVariantId;
    const threshold = this.#giftThreshold;
    const eligibleSubtotal = getEligibleSubtotal(cart, giftVariantId);
    const isEligible = threshold <= 0 || eligibleSubtotal >= threshold;
    const giftQuantity = getVariantQuantity(cart, giftVariantId);

    if ((isEligible && giftQuantity > 0) || (!isEligible && giftQuantity === 0)) return;

    this.#isUpdatingGift = true;
    let standardUpdate;

    try {
      const sectionIds = getCartSectionIds();
      let nextCart;
      let sections = {};

      if (isEligible) {
        standardUpdate = beginCartLinesUpdate(this, {
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: giftVariantId, quantity: 1 }],
          detail: { source: 'ch-product-upsells-free-gift', variantId: giftVariantId },
        });
        const addResponse = await fetchJson(CART_ADD_URL, {
          method: 'POST',
          body: JSON.stringify({
            id: giftVariantId,
            quantity: 1,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        nextCart = await this.#fetchCart();
        sections = addResponse.sections || {};
        standardUpdate.resolve(nextCart, { sections, itemCount: nextCart.item_count });
      } else {
        const giftLine = controllableLineForVariant(cart, giftVariantId);
        if (!giftLine?.key) throw new Error('Unable to identify the promotional gift line.');
        standardUpdate = beginCartLinesUpdate(this, {
          action: 'remove',
          context: 'product',
          lines: [{ id: giftLine.key, quantity: 0 }],
          detail: { source: 'ch-product-upsells-free-gift', variantId: giftVariantId },
        });
        nextCart = await fetchJson(CART_CHANGE_URL, {
          method: 'POST',
          body: JSON.stringify({
            id: giftLine.key,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        sections = nextCart.sections || {};
        standardUpdate.resolve(nextCart, { sections, itemCount: nextCart.item_count });
      }

      this.#updateFromCart(nextCart);
    } catch (error) {
      standardUpdate?.reject(error);
      console.error('Failed to sync free gift:', error);
    } finally {
      this.#isUpdatingGift = false;
    }
  }

  #setCardUpdating(card, isUpdating) {
    card.toggleAttribute('aria-busy', isUpdating);
    const button = card.querySelector('[data-ch-product-upsell-toggle]');
    if (button instanceof HTMLButtonElement) button.disabled = isUpdating;
  }

  #showFeedback(card) {
    card.dataset.feedback = 'true';
    this.#setCardStatus(card, 'Added');

    const previousTimeout = this.#feedbackTimeouts.get(card);
    if (previousTimeout) window.clearTimeout(previousTimeout);

    const timeout = window.setTimeout(() => {
      delete card.dataset.feedback;
      this.#setCardStatus(card, '');
      this.#feedbackTimeouts.delete(card);
    }, SUCCESS_DELAY);

    this.#feedbackTimeouts.set(card, timeout);
  }

  #setCardStatus(card, message) {
    const status = card.querySelector('[data-ch-product-upsell-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('ch-product-upsells')) {
  customElements.define('ch-product-upsells', CHProductUpsells);
}
